// @ts-nocheck

import { reduce, map, groupBy, size } from 'lodash';
import { createSelector } from 'reselect';

import { DE_GAIN_TAX_RATIO } from '../../constants/tax';
import {
  selectBasketBonuses,
  selectBasketEligibleBonuses,
  selectBasketEligibleBooster,
  selectBasketIsCashout,
  selectBasketIsFromFreebets,
  selectBasketItems,
  selectBasketMode,
  selectBasketSelectedBonusId,
  selectBasketStake,
  selectBasketStatus,
  selectBasketSelectedFreebetId,
  selectBasketSystems,
} from '../../selectors/basket/basketPrimary.selector';
import { selectBasketTotalCostSimple, makeBasketItemIdSelector } from '../../selectors/basketCommon.selector';
import { selectBasketNbBetsSystem, selectBasketTotalCostSystem } from '../../selectors/basketCommon.selector';
import {
  selectBasketEnhancedItems,
  selectBasketItemsWithSelectedBonusesValues,
} from '../../selectors/basketItems.selector';
import { selectRuntimeIsCashoutEnabled, selectRuntimeLicence } from '../../selectors/runtime/runtimePrimary.selector';
import { BasketModes } from '../../types/basket';
import { basket as BasketUtils, number as NumberUtils } from '../../utils';

const getBasketEligibleBoosterGain = createSelector(
  selectBasketEligibleBooster,
  (basketEligibleBooster) => basketEligibleBooster?.boosterGain || 0,
);

const getSummarySimple = createSelector(
  [
    selectBasketEnhancedItems,
    selectBasketStake,
    selectBasketTotalCostSimple,
    selectBasketItemsWithSelectedBonusesValues,
  ],
  (items, stake, totalCost, selectedBonusesValues) => {
    let totalPotentialGain = reduce(items, (acc, { potentialGain: x }) => Math.round((acc + x) * 100) / 100, 0);
    // Filter selectedBonusesValues in order to return only bonuses matching displayed items (e.g. confirmation screen)
    const filterSelectedBonusId = map(items, (x) => x.selectedBonusId);
    const finalSelectedBonusesValues = selectedBonusesValues.filter(
      (promo) => filterSelectedBonusId.indexOf(promo.promotionId) !== -1,
    );

    if (finalSelectedBonusesValues.length > 0) {
      const bonusGain = reduce(items, (acc, { possibleBonus: y }) => Math.round((acc + y) * 100) / 100, 0);
      totalPotentialGain += bonusGain;
    }

    let totalOdd = reduce(items, (acc, { value, validatedValue }) => acc * (validatedValue || value), 1);
    totalOdd = Math.round(totalOdd * 100) / 100;

    return {
      totalCost,
      totalPotentialGain,
      minusDEGainTaxValue: totalPotentialGain * DE_GAIN_TAX_RATIO,
      selectedBonusesValues: finalSelectedBonusesValues,
      nbBet: items.length,
      totalOdd,
    };
  },
);

const getSummaryCombined = createSelector(
  [
    selectBasketEnhancedItems,
    selectBasketStake,
    selectBasketSelectedFreebetId,
    selectBasketSelectedBonusId,
    selectBasketEligibleBonuses,
    getBasketEligibleBoosterGain,
    selectBasketIsFromFreebets,
  ],
  (items, stake, selectedFreebetId, selectedBonusId, eligibleBonuses, basketBoosterGain, isFromFreeBets) => {
    const totalPotentialGainBooster = 0;
    const totalPotentialGainBonus = 0;
    let boosterGain = 0;
    let bonusGain = 0;
    let potentialGain = 0;
    let totalOdd = 1;
    let totalCost = 0;

    totalCost = parseFloat(stake) || 0;
    totalOdd = reduce(items, (acc, { value, validatedValue }) => acc * (validatedValue || value), 1);
    potentialGain = BasketUtils.computePotentialGain(totalOdd, stake, !!selectedFreebetId || isFromFreeBets);
    totalOdd = Math.round(totalOdd * 100) / 100;

    let previousTotalOdd = reduce(items, (acc, { value, previousValue }) => acc * (previousValue || value), 1);
    previousTotalOdd = Math.round(previousTotalOdd * 100) / 100;

    boosterGain = basketBoosterGain;

    if (selectedBonusId && eligibleBonuses.filter((x) => x.promotionId === selectedBonusId).length) {
      bonusGain = eligibleBonuses.filter((x) => x.promotionId === selectedBonusId)[0].possibleBonus;
    }

    bonusGain = Math.round(bonusGain * 100) / 100;
    boosterGain = Math.round(boosterGain * 100) / 100;
    potentialGain = Math.round(potentialGain * 100) / 100;

    // on affiche les gains booster dans le total
    const totalPotentialGain = potentialGain + bonusGain + boosterGain;

    const nbBet = items.reduce((acc, item) => {
      const { selectionItems: itemSelectionItems } = item;

      if (itemSelectionItems?.length) {
        return (
          acc +
          itemSelectionItems.reduce((_acc, selectionItem) => {
            const { outcomes: selectionItemOutcomes } = selectionItem;

            if (selectionItemOutcomes?.length) {
              return _acc + selectionItemOutcomes.length;
            }

            return _acc + 1;
          }, 0)
        );
      }

      return acc + 1;
    }, 0);

    return {
      totalCost,
      totalPotentialGainBonus,
      totalPotentialGainBooster,
      minusDEGainTaxValue: totalPotentialGain * DE_GAIN_TAX_RATIO,
      totalPotentialGain,
      boosterGain,
      bonusGain,
      totalOdd,
      nbBet,
      previousTotalOdd,
    };
  },
);

const getSummarySystem = createSelector(
  [
    selectBasketEnhancedItems,
    selectBasketStake,
    selectBasketSystems,
    selectBasketNbBetsSystem,
    selectBasketTotalCostSystem,
    getBasketEligibleBoosterGain,
    selectBasketSelectedFreebetId,
  ],
  (items, stake, systems, basketNbBetSystem, basketTotalCost, basketBoosterGain, selectedFreeBetId) => {
    let nbBet = 0;
    let totalPotentialGain = 0;
    let boosterGain = 0;
    let totalCost = 0;
    let totalOdd = 1;

    nbBet = basketNbBetSystem;
    totalCost = basketTotalCost;
    totalPotentialGain = reduce(
      systems,
      (acc, { checked, k }) => {
        if (!checked) {
          return acc;
        }
        let combinations = null;
        const { bankers, nobankers } = groupBy(items, ({ banker }) => (banker ? 'bankers' : 'nobankers'));
        const nobankersOdds = map(nobankers, ({ value, validatedValue }) => validatedValue || value);
        const bankersOdds = map(bankers, ({ value, validatedValue }) => validatedValue || value);
        if (size(nobankers) > 0) {
          combinations = NumberUtils.getCombinations(nobankersOdds, k).map((x) => x.concat(bankersOdds));
        } else {
          combinations = NumberUtils.getCombinations(bankersOdds, bankersOdds.length);
        }
        return (
          acc +
          reduce(
            combinations,
            (value, combination) => {
              const odd = reduce(combination, (a, b) => a * b, 1);
              return value + BasketUtils.computePotentialGain(odd, stake, !!selectedFreeBetId);
            },
            0,
          )
        );
      },
      0,
    );
    boosterGain = basketBoosterGain;
    totalPotentialGain = totalPotentialGain + boosterGain;
    totalOdd = totalPotentialGain / stake;
    totalOdd = Math.round(totalOdd * 100) / 100;

    return {
      totalCost,
      totalPotentialGain,
      minusDEGainTaxValue: totalPotentialGain * DE_GAIN_TAX_RATIO,
      nbBet,
      boosterGain,
      totalOdd,
    };
  },
);

const selectBasketSummaryProps = createSelector(
  [
    selectBasketMode,
    selectBasketStatus,
    selectBasketEligibleBonuses,
    selectBasketSelectedBonusId,
    selectBasketEligibleBooster,
    selectBasketBonuses,
    selectBasketStake,
    getSummarySimple,
    getSummaryCombined,
    getSummarySystem,
    selectBasketIsCashout,
    selectRuntimeIsCashoutEnabled,
    selectBasketEnhancedItems,
    selectBasketIsFromFreebets,
    selectBasketItems,
    makeBasketItemIdSelector(),
    selectRuntimeLicence,
  ],
  (
    mode,
    status,
    eligibleBonuses,
    selectedBonusId,
    eligibleBooster,
    bonuses,
    stake,
    summarySimple,
    summaryCombined,
    summarySystem,
    isCashout,
    isCashoutEnabled,
    items,
    isFromFreeBets,
    basketItems,
    oddId,
    licence,
  ) => {
    let summary = {
      totalPotentialGain: 0,
      totalPotentialGainBooster: 0,
      totalPotentialGainBonus: 0,
      boosterGain: 0,
      bonusGain: 0,
      totalOdd: 1,
      totalCost: 0,
      nbBet: 0,
    };

    const oddsHasChanged = reduce(items, (acc, { changed }) => acc || !!changed, false);
    const selectedFreebetId = basketItems?.[oddId]?.selectedFreebetId;

    if (mode === BasketModes.SIMPLE) {
      summary = { ...summary, ...summarySimple };
    } else if (mode === BasketModes.COMBINED) {
      summary = { ...summary, ...summaryCombined };
    } else if (mode === BasketModes.SYSTEM) {
      summary = { ...summary, ...summarySystem };
    }

    return {
      ...summary,
      mode,
      status,
      eligibleBonuses,
      selectedBonusId,
      eligibleBooster,
      isCashout,
      stake,
      bonuses,
      isCashoutEnabled,
      selectedFreebetId,
      oddsHasChanged,
      isFromFreeBets,
      licence,
    };
  },
);

export default selectBasketSummaryProps;
